/**
 * useIntentIntelligence — SLICE 4: the lifecycle hook that wires the three
 * Intent-Intelligence layers together for a live editing surface.
 *
 * It owns the COST asymmetry between the layers:
 *   - The deterministic layers (`suggestProjectIntent` + `rankNextInputs`) are
 *     FREE (pure, no network) → recompute on EVERY relevant change.
 *   - The LLM layer (`inferIntentFromContext`) costs a model call → re-run ONLY
 *     when a CONTEXT SOURCE's content hash changes (new/edited site-context
 *     prose, a newly-tagged doc, a recompiled SED). Edits that don't touch the
 *     inference inputs (e.g. the user typing into an unrelated form field) must
 *     NOT trigger inference.
 *
 * The hook exposes:
 *   - `suggestions`    — the merged `SuggestedFullIntent` (deterministic ∪ LLM).
 *   - `nextBestInput`  — the top `rankNextInputs` recommendation.
 *   - `isInferring`    — true while an LLM inference is in flight.
 *   - `reInfer()`      — force a re-inference (bypasses the hash gate, e.g. a
 *                        manual "refresh AI suggestions" button).
 *
 * USER ALWAYS WINS: the merge layer (`mergeIntentSuggestions`) is the single
 * choke point that excludes any field the user authored, so re-inference can
 * never re-surface a value the user owns.
 *
 * Determinism: the deterministic outputs are byte-stable for identical inputs;
 * the LLM result is content-hash-gated + debounced so the same context never
 * re-fires. Tests MOCK `inferIntentFromContext` (no real API call).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ProjectIntent } from '../types/siteRag';
import type { SiteEntity } from '../types/site';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import {
  suggestProjectIntent,
  type SuggestProjectIntentInput,
} from '../lib/suggestProjectIntent';
import { rankNextInputs, type NextInputRecommendation } from '../lib/intentIntelligence/nextBestInput';
import {
  mergeIntentSuggestions,
  type SuggestedFullIntent,
} from '../lib/intentIntelligence/mergeSuggestions';
import {
  inferIntentFromContext,
  type SuggestedFields,
  type BuildIntentInferenceContextInput,
} from '../services/inferIntentFromContext';

/** Default debounce before an LLM re-inference fires, ms. */
const DEFAULT_INFER_DEBOUNCE_MS = 1200;

export interface UseIntentIntelligenceInput {
  /** The site's current intent — the single source of truth + USER-WINS gate. */
  intent: ProjectIntent | null | undefined;
  /** The site (drives archetype + the deterministic suggester's context). */
  site?: SiteEntity | null;
  /** Optional extra deterministic-suggester context (recent reports / docs / …). */
  suggesterContext?: Omit<SuggestProjectIntentInput, 'site' | 'currentIntent' | 'archetype'>;
  /** The compiled SED — recompiles re-arm inference (content-hash-gated). */
  sed?: SiteEvidenceDocument | null;
  /** The inference context inputs (SED prose / site-context prose / claims / docs). */
  inferenceContext?: Omit<BuildIntentInferenceContextInput, 'partialIntent'>;
  /** Disable the LLM layer entirely (deterministic-only). Default false. */
  disableInference?: boolean;
  /** Override the debounce window (ms). */
  inferDebounceMs?: number;
  /**
   * Which Intent view is showing. The screening brief ranks only its own
   * essentials; the Design & report assumptions view ranks the full set, so
   * its "next input" never points at a field the view does not render.
   * Default 'screening'.
   */
  stage?: 'screening' | 'detailed';
}

export interface UseIntentIntelligenceResult {
  /** Merged deterministic ∪ LLM suggestions (user-authored fields excluded). */
  suggestions: SuggestedFullIntent;
  /** The single highest-value field to fill next, or null when none remain. */
  nextBestInput: NextInputRecommendation | null;
  /** True while an LLM inference is in flight. */
  isInferring: boolean;
  /** Force a re-inference now (bypasses the content-hash gate). */
  reInfer: () => void;
}

/**
 * Stable, order-independent content hash over the inference context. A change
 * here (new/edited SED, site-context prose, claims, docs) is the ONLY thing
 * that re-arms an LLM inference — unrelated form edits don't change it, so
 * they don't cost a model call. Pure string hash (FNV-1a) — deterministic.
 */
export function hashInferenceContext(
  ctx: Omit<BuildIntentInferenceContextInput, 'partialIntent'> | undefined,
  sed: SiteEvidenceDocument | null | undefined,
): string {
  if (!ctx && !sed) return '';
  const sedText = ctx?.sed ?? '';
  const proseText = ctx?.siteContextProse ?? '';
  const hasClaims = (ctx?.claims?.length ?? 0) > 0;
  const hasDocs = (ctx?.docs?.length ?? 0) > 0;
  if (!sed && !sedText.trim() && !proseText.trim() && !hasClaims && !hasDocs) return '';
  const parts: string[] = [];
  // SED: prefer an explicit version/hash if the doc carries one, else its text.
  const sedVersion =
    (sed as { contentHash?: string; updatedAt?: string; version?: number } | null | undefined)?.contentHash ??
    (sed as { updatedAt?: string } | null | undefined)?.updatedAt ??
    String((sed as { version?: number } | null | undefined)?.version ?? '');
  parts.push(`sed:${sedVersion}:${sedText}`);
  parts.push(`prose:${proseText}`);
  if (ctx?.claims) {
    parts.push(
      'claims:' +
        ctx.claims
          .map((c) => `${c.claim}=${c.claimedValue ?? ''}`)
          .join('|'),
    );
  }
  if (ctx?.docs) {
    parts.push(
      'docs:' +
        ctx.docs
          .map((d) => `${d.fileName}#${(d.tags ?? []).join(',')}#${d.summary ?? ''}`)
          .join('|'),
    );
  }
  return fnv1a(parts.join(''));
}

/** FNV-1a 32-bit hash → hex. Small, dependency-free, deterministic. */
function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16);
}

export function useIntentIntelligence(
  input: UseIntentIntelligenceInput,
): UseIntentIntelligenceResult {
  const {
    intent,
    site,
    suggesterContext,
    sed,
    inferenceContext,
    disableInference = false,
    inferDebounceMs = DEFAULT_INFER_DEBOUNCE_MS,
    stage = 'screening',
  } = input;
  const effectiveDisableInference = disableInference || Boolean(site?.draftOnly);

  // ── Deterministic layers — recompute on every relevant change (FREE) ──
  const deterministic = useMemo(() => {
    return suggestProjectIntent({
      ...(suggesterContext ?? {}),
      site: site ?? undefined,
      archetype: site?.archetypeOverride,
      currentIntent: intent ?? undefined,
    });
  }, [suggesterContext, site, intent]);

  const ranking = useMemo(
    () => rankNextInputs(intent, site, sed, { stage }),
    [intent, site, sed, stage],
  );

  // ── LLM layer — content-hash-gated, debounced ──
  const [llm, setLlm] = useState<SuggestedFields>({});
  const [isInferring, setIsInferring] = useState(false);

  // The hash of the context that produced the CURRENT `llm` result. We re-run
  // only when the live hash differs from this. `reInfer` clears it to force.
  const lastInferredHashRef = useRef<string | null>(null);
  // Guards against a stale async result overwriting a newer one (race).
  const inferSeqRef = useRef(0);
  // Debounce timer handle.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentHash = useMemo(
    () => hashInferenceContext(inferenceContext, sed),
    [inferenceContext, sed],
  );

  const runInference = useCallback(
    async (hashAtSchedule: string) => {
      if (effectiveDisableInference) return;
      const seq = ++inferSeqRef.current;
      setIsInferring(true);
      try {
        const result = await inferIntentFromContext({
          ...(inferenceContext ?? {}),
          partialIntent: intent ?? undefined,
        });
        // Ignore if a newer inference superseded this one.
        if (seq !== inferSeqRef.current) return;
        setLlm(result);
        lastInferredHashRef.current = hashAtSchedule;
      } catch {
        // Degrade silently — deterministic layer still covers the gaps.
        if (seq === inferSeqRef.current) setLlm({});
      } finally {
        if (seq === inferSeqRef.current) setIsInferring(false);
      }
    },
    // `intent` is intentionally included: re-inference reads the partial intent
    // to skip user-decided fields. But a pure intent change does NOT re-arm
    // inference on its own (see the effect's gate on `currentHash`).
    [effectiveDisableInference, inferenceContext, intent],
  );

  // Schedule a debounced inference whenever the CONTENT HASH changes.
  useEffect(() => {
    if (effectiveDisableInference) return;
    // Nothing to infer from → clear any stale LLM result.
    if (currentHash === '') {
      setLlm((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      lastInferredHashRef.current = '';
      return;
    }
    // Already inferred for this exact context → skip (cost guard).
    if (lastInferredHashRef.current === currentHash) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void runInference(currentHash);
    }, inferDebounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // `runInference` is stable per its deps; `currentHash` is the gate.
  }, [currentHash, effectiveDisableInference, inferDebounceMs, runInference]);

  // Force a re-inference: clear the gate + run immediately (no debounce).
  const reInfer = useCallback(() => {
    if (effectiveDisableInference) return;
    lastInferredHashRef.current = null;
    if (timerRef.current) clearTimeout(timerRef.current);
    void runInference(currentHash);
  }, [effectiveDisableInference, runInference, currentHash]);

  // ── Merge — the single USER-WINS choke point ──
  const suggestions = useMemo(
    () => mergeIntentSuggestions(deterministic, llm, intent ?? undefined),
    [deterministic, llm, intent],
  );

  return {
    suggestions,
    nextBestInput: ranking.best,
    isInferring,
    reInfer,
  };
}
